import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { useTable, useSortBy, Column, usePagination } from 'react-table';
import '../../settings/settings.css';
import exportTableToPDF, { ExportColumn } from '../../utils/ExportTableToPDF';
import exportTableToExcel from '../../utils/ExportTableToExcel';
import CustomModal from './CustomModal';
import { TableConfigurator, getAllColumnsConfig } from '../TableConfigurator';
import { report } from 'process';
import { CurrencyNumberOutput } from './FormattedOutputs';
import { GenericAttrDisplay } from '../../masterdata/components/AttributePicker';
import { displayDate } from '../../utils/MiscFunctions';
import BeatLoaderImport from 'react-spinners/BeatLoader';

// react-spinners has no "exports" map in its package.json, so `react-spinners/BeatLoader`
// resolves as a plain CJS deep import. Depending on how a given bundle/chunk interops
// that CJS module for a default import, `BeatLoaderImport` can come through already
// unwrapped (a function) or still wrapped one level deeper (only `.default` is the
// function) - same class of bug as react-ga4/lottie-react. Resolve defensively so both
// shapes work.
const BeatLoader: typeof BeatLoaderImport =
  typeof BeatLoaderImport === 'function'
    ? BeatLoaderImport
    : (BeatLoaderImport as unknown as { default: typeof BeatLoaderImport }).default;



const SIZE_CONFIG = {
  large: 3,   // relative size unit
  medium: 2,  // relative size unit
  small: 1,    // relative size unit
  "extra-small": 0.5 // relative size unit (wrapped in quotes)
};

interface DataTableProps<T extends object> {
  columns: Column<T>[];
  object?: string;
  screenName?: string;
  subObject?: string;
  data: T[];
  title?: string;
  showToolBar?: boolean;
  onRowClick?: (row: T) => void;
  selectedRows?: T[]; // New prop for tracking selected rows
  onSelectedRowsChange?: (rows: T[]) => void;
  showConfigureOption?: boolean;
  updateColumns?: (columns: Column<T>[]) => Column<T>[]
  headerToolbar?: ReactNode;
  hidePagination?: boolean;
  disableSort?: boolean;
  isRefreshing?: boolean;
  showMultiSelect?: boolean
  showTotal?: boolean;
  showZeroAsHyphen?: boolean;
  exportData?: T[];
  resetKey?: number;
  periodLabel?: string;
  emptyStateActions?: { label: string; onClick: () => void }[];
}

export const calculateColumnWidths = (columns: { size: string | number; }[], SIZE_CONFIG: { [x: string]: any; }) => {
  const totalSizeUnits = columns.reduce((total: any, column: { size: string | number; }) => {
    const size = column.size ? SIZE_CONFIG[column.size] : 1;
    return total + size;
  }, 0);

  return columns.map((column: { size: string | number; }) => {
    const size = column.size ? SIZE_CONFIG[column.size] : 1;
    const widthPercentage = (size / totalSizeUnits) * 100;
    return {
      ...column,
      width: `${widthPercentage}%`
    };
  });
};

export const DataTable = <T extends object>({ columns: defaultColumns,
  object, screenName, subObject, title = object, data, showToolBar = true, onRowClick, showZeroAsHyphen = false,
  selectedRows: initialSelectedRows = [],
  onSelectedRowsChange,
  isRefreshing,
  updateColumns, headerToolbar, showConfigureOption = false, showMultiSelect, disableSort, hidePagination, showTotal, exportData, resetKey, periodLabel, emptyStateActions }: DataTableProps<T>) => {
  const [columns, setColumns] = useState<Column<T>[]>(defaultColumns);
  const [reportColumns, setReportColumns] = useState<Column[]>(defaultColumns as Column[])
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filteredData, setFilteredData] = useState<T[]>(data || []);
  const [pageSize, setPageSize] = useState(30);
  const [showConfigureModal, setShowConfigureModal] = useState(false);
  const [triggerRefresh, setTriggerRefresh] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<Column<T>[]>(defaultColumns);
  const [selectedRows, setSelectedRows] = useState<T[]>(initialSelectedRows);
  const [showConfigHint, setShowConfigHint] = useState<boolean>(true);

  // Reset internal selection state when parent increments resetKey (avoids costly full remount)
  useEffect(() => {
    if (resetKey !== undefined && resetKey > 0) {
      setSelectedRows([]);
      if (onSelectedRowsChange) onSelectedRowsChange([]);
    }
  }, [resetKey]);
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const colWithUpdates = useMemo(() =>
    (updateColumns ? updateColumns(reportColumns as Column<T>[]) : reportColumns) as Column<T>[],
  [reportColumns]);

  const columnsWithWidth = useMemo(() => {
    const visibleCols = colWithUpdates.filter(column => column.isVisible !== false);

    const totalSizeUnits = visibleCols.reduce((total, column) => {
      const size = column.size ? SIZE_CONFIG[column.size] : 1;
      return total + size;
    }, 0);

    return visibleCols.map(column => {
      const size = column.size ? SIZE_CONFIG[column.size] : 1;
      const widthPercentage = (size / totalSizeUnits) * 100;
      return {
        ...column,
        width: `${widthPercentage}%`
      };
    });
  }, [colWithUpdates]);

  // MBAI-60: restore sort order from localStorage per screen
  const sortStorageKey = screenName ? `dt-sort-${screenName}` : null;
  const savedSort = useMemo(() => {
    if (!sortStorageKey) return [];
    try { return JSON.parse(localStorage.getItem(sortStorageKey) || '[]'); } catch { return []; }
  }, [sortStorageKey]);

  const { getTableProps, getTableBodyProps, headerGroups, page, prepareRow, canPreviousPage, canNextPage, pageOptions, gotoPage, nextPage, previousPage, setPageSize: setTablePageSize, state: { pageIndex, sortBy } } = useTable(
    { columns: columnsWithWidth as Column<T>[], data: filteredData, initialState: { pageSize, sortBy: savedSort } },
    useSortBy,
    usePagination
  );

  // MBAI-60: persist sort changes to localStorage
  useEffect(() => {
    if (sortStorageKey) {
      localStorage.setItem(sortStorageKey, JSON.stringify(sortBy));
    }
  }, [sortBy, sortStorageKey]);

  useEffect(() => {
    setFilteredData(data ? data : []);
  }, [data]);

  useEffect(() => {
    setTablePageSize(pageSize);
  }, [pageSize, setTablePageSize]);

  // Add this helper function at the top of your DataTable component, before the component definition
  const getCustomFieldValue = (row: any, fieldName: string) => {
    // Function to search for custom field in a given customFields array
    const findCustomField = (customFields: any[]) => {
      if (!customFields || !Array.isArray(customFields)) {
        return null;
      }
      return customFields.find((field: any) => field.fieldName === fieldName);
    };

    // Check direct customFields first
    let customField = findCustomField(row.customFields);
    if (customField) {
      return customField.fieldValue || '';
    }

    // Check txnHeader.customFields
    if (row.txnHeader) {
      customField = findCustomField(row.txnHeader.customFields);
      if (customField) {
        return customField.fieldValue || '';
      }
    }

    // Check paymentHeader.customFields
    if (row.paymentHeader) {
      customField = findCustomField(row.paymentHeader.customFields);
      if (customField) {
        return customField.fieldValue || '';
      }
    }

    // You can add more nested paths here if needed
    // Example: row.someOtherHeader.customFields

    return '';
  };

  useEffect(() => {
    const getReportColumns = async () => {
      const reportColumnFromConfig = await getAllColumnsConfig(object || '', screenName || '', subObject);
      if (reportColumnFromConfig.length > 0) {
        // Server-side fieldNames may be dotted paths into the raw entity (e.g. 'txnHeader.txnNo'),
        // while frontend defaultColumns use bare ids (e.g. 'txnNo') since they use function accessors.
        // Fall back to comparing the last path segment so these still match instead of rendering as
        // separate duplicate columns.
        const isSameField = (rcAccessor: any, defaultCol: any) => {
          if (rcAccessor === defaultCol.id) return true;
          if (typeof defaultCol.accessor === 'string' && rcAccessor === defaultCol.accessor) return true;
          const lastSegment = typeof rcAccessor === 'string' && rcAccessor.includes('.') ? rcAccessor.split('.').pop() : undefined;
          return lastSegment !== undefined && lastSegment === defaultCol.id;
        };

        // For defaultColumns that match a server-config entry by Header, inherit sortOrder
        // (and visibility, unless the defaultColumn hard-forces isVisible:false).
        // This ensures user-configured column order is respected for prop-defined columns like "Amount".
        const mergedDefaultColumns = defaultColumns.map(defaultCol => {
          const matchingConfig = reportColumnFromConfig.find(rc => isSameField(rc.accessor, defaultCol as any));
          if (!matchingConfig) return defaultCol;
          const inheritVisibility = (defaultCol as any).isVisible !== false;
          return {
            ...defaultCol,
            sortOrder: matchingConfig.sortOrder,
            ...(inheritVisibility ? { isVisible: matchingConfig.isVisible } : {}),
          };
        });

        // Filter out columns from reportColumnFromConfig where the accessor exists in defaultColumns
        const filteredReportColumns = reportColumnFromConfig.filter(
          (reportCol) => !defaultColumns.some((defaultCol) => isSameField(reportCol.accessor, defaultCol as any))
        );

        // Map the filtered columns to set custom Cell renderers
        const updatedColumns = filteredReportColumns.map((col: any) => {
          if (col.isCustomField) {
            if (col.fieldType === 'date') {
              return {
                ...col,
                Cell: ({ row }: { row: any }) => {
                  const value = getCustomFieldValue(row.original, col.fieldName);
                  return value ? displayDate(new Date(value)) : 'N/A';
                },
              };
            }
            if (col.fieldType === 'currency') {
              return {
                ...col,
                Cell: ({ row }: { row: any }) => {
                  const value = getCustomFieldValue(row.original, col.fieldName);
                  return value !== undefined && value !== null && value !== '' ? (
                    <CurrencyNumberOutput
                      amount={parseFloat(value)}
                      currencyId={row.original?.txnHeader?.currencyId || row.original?.paymentHeader?.currencyId}
                      className="currency-output"
                    />
                  ) : (
                    ''
                  );
                },
              };
            }
            // Default case for custom fields (text, number, etc.)
            return {
              ...col,
              Cell: ({ row }: { row: any }) => {
                const value = getCustomFieldValue(row.original, col.fieldName);
                return value || '';
              },
            };
          }

          // Handle regular (non-custom) fields
          if (col.fieldType === 'date') {
            return {
              ...col,
              Cell: ({ value }: { value: Date | undefined }) =>
                value ? displayDate(new Date(value)) : 'N/A',
            };
          }
          if (col.fieldType === 'currency') {
            return {
              ...col,
              Cell: ({ value, row }: { value: number | undefined, row: any }) =>
                value !== undefined && value !== null ? (
                  <CurrencyNumberOutput
                    amount={value}
                    currencyId={row.original?.txnHeader?.currencyId || row.original?.paymentHeader?.currencyId}
                    className="currency-output"
                    showZeroAsHyphen={showZeroAsHyphen}
                  />
                ) : (
                  ''
                ),
            };
          }

          return col;
        });

        // Combine mergedDefaultColumns and updatedColumns, then sort by the resolved sortOrder
        const combinedColumns = [...mergedDefaultColumns, ...updatedColumns];
        const sortedColumns = combinedColumns.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
        // Set the final combined columns
        setReportColumns(sortedColumns as Column<any>[]);
      } else {
        // If no reportColumnFromConfig, fallback to defaultColumns
        setReportColumns(defaultColumns as Column<any>[]);
      }
    };

    getReportColumns();
  }, [object, screenName, defaultColumns, triggerRefresh]);



  const exportColumns = colWithUpdates
    .filter(column => column.disableExport !== true && (column.isVisible !== false || column.exportOnly === true))
    .map(column => {
      const col = column as any;
      const accessor = col.isCustomField && col.fieldName
        ? (row: T) => getCustomFieldValue(row, col.fieldName)
        : column.accessor as keyof T;
      return {
        Header: typeof column.Header === 'string' ? column.Header : '',
        accessor,
        className: column.className,
        fieldType: column?.fieldType
      };
    }) as ExportColumn<T>[];

  const handleSearchTerm = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearchTerm(value);
    if (value.length > 0) {
      const lowercasedFilter = value.toLowerCase();
      const filteredData = data.filter(item =>
        Object.values(item).some(
          v => v !== null && v !== undefined && JSON.stringify(v).toString().toLowerCase().includes(lowercasedFilter)
        )
      );
      setFilteredData(filteredData);
    } else {
      setFilteredData(data ? data : []);
    }
  };

  useEffect(() => {
    if (searchTerm.length > 0) {
      const lowercasedFilter = searchTerm.toLowerCase();
      const filteredData = data.filter(item =>
        Object.values(item).some(
          v => v !== null && v !== undefined && JSON.stringify(v).toString().toLowerCase().includes(lowercasedFilter)
        )
      );
      setFilteredData(filteredData);
    }
  }, [searchTerm, data])

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPageSize(Number(e.target.value));
  };

  const toggleModal = () => {
    setShowConfigureModal(!showConfigureModal)
  }

  const closeModal = () => {
    setShowConfigureModal(false)
  }

  // Handle row selection (checkbox)
  const toggleRowSelection = (row: T) => {
    // Use JSON comparison for object equality
    const rowString = JSON.stringify(row);
    const isSelected = selectedRows.some(
      (selectedRow) => JSON.stringify(selectedRow) === rowString
    );

    const updatedSelectedRows = isSelected
      ? selectedRows.filter((selectedRow) => JSON.stringify(selectedRow) !== rowString)
      : [...selectedRows, row];

    setSelectedRows(updatedSelectedRows);
    if (onSelectedRowsChange) {
      onSelectedRowsChange(updatedSelectedRows);
    }
  };

  const handleColumnVisibilityChange = (columnId: string) => {
    // setVisibleColumns(prevColumns =>
    //   prevColumns.map(col =>
    //     col.id === columnId ? { ...col, isVisible: !col.isVisible } : col
    //   ).filter(col => col.isVisible !== false)
    // );
  };

  function handleSave(tableColumns: Column[], save: boolean): void {
    setTriggerRefresh(!triggerRefresh)
    if (save) {
      setShowConfigureModal(false)
    }
  }

  const handleSelectAll = (isAllSelected: boolean) => {
    const allRows = isAllSelected ? filteredData : [];
    setSelectedRows(allRows);
    if (onSelectedRowsChange) {
      onSelectedRowsChange(allRows);
    }
  };

  // Add function to calculate totals
  // const calculateTotals = () => {
  //   if (!showTotal) return null;

  //   const totals: { [key: string]: number } = {};

  //   columnsWithWidth.forEach((column: any) => {
  //     if (column.total) {
  //       totals[column.accessor as string] = filteredData.reduce((sum, row) => {
  //         const value = row[column.accessor as keyof T];
  //         return sum + (typeof value === 'number' ? value : 0);
  //       }, 0);
  //     }
  //   });
  //   return totals;
  // };

  const totals = useMemo(() => {
    if (!showTotal) return null;

    const getNestedValue = (obj: any, path: string): any =>
      path.split('.').reduce((o, k) => (o && o[k] !== undefined ? o[k] : 0), obj);

    const totals: { [key: string]: number } = {};

    columnsWithWidth.forEach((column: any) => {
      if (column.total && typeof column.accessor === 'string') {
        totals[column.accessor] = filteredData.reduce((sum, row) => {
          const value = getNestedValue(row, column.accessor);
          return sum + (typeof value === 'number' ? value : 0);
        }, 0);
      }
    });




    return totals;
  }, [filteredData, columnsWithWidth, showTotal]);



  return (
    <>
      {showToolBar &&
        <div className="d-flex justify-content-between mb-3 align-items-center">
          <div className="d-flex align-items-top w-75 " style={{}}>
            {/* MBAI-461: record count shown alongside search box */}
            <div className="position-relative me-2 mt-1" style={{ minWidth: '100px', maxWidth: '250px' }}>
              <input
                type="text"
                className="form-control"
                placeholder={'Search'.toTranslatedDisplay()}
                value={searchTerm || ''}
                onChange={handleSearchTerm}
                style={{ height: '43px', paddingRight: filteredData.length > 0 ? '80px' : undefined }}
              />
              {filteredData.length > 0 && (
                <span
                  className="position-absolute top-50 translate-middle-y text-muted fs-8 pe-2"
                  style={{ right: 0, pointerEvents: 'none', fontSize: '11px' }}
                >
                  {filteredData.length}
                </span>
              )}
            </div>
            <div className='py-1'>
              {headerToolbar}
            </div>

          </div>
          <div className='text-end w-100'>
            <button className="btn btn-sm btn-secondary export-btn" onClick={() => exportTableToPDF(exportColumns, exportData || data, `${title}.pdf`, title, totals ?? undefined, periodLabel)}>
              <i className="fas fa-file-pdf"></i>
              <span className="export-text">{'Export as PDF'.toTranslatedDisplay()}</span>
            </button>
            <button
              className="ms-2 btn btn-sm btn-secondary export-btn"
              onClick={() => exportTableToExcel(exportColumns, exportData || data, `${title}.xlsx`, title, totals ?? undefined, periodLabel)}
            >
              <i className="fas fa-file-excel"></i>
              <span className="export-text">{'Export as Excel'.toTranslatedDisplay()}</span>
            </button>
            {showConfigureOption && (
              <button
                className={`ms-2 btn btn-sm btn-secondary export-btn`}
                onClick={() => {
                  setShowConfigHint(false);
                  toggleModal();
                }}
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title={'Customize which columns to display in this report'.toTranslatedDisplay()}
                onMouseEnter={() => setShowConfigHint(false)}
              >
                <i className="fas fa-gear"></i>
                <span className="export-text">{'Configure Columns'.toTranslatedDisplay()}</span>
              </button>
            )}
          </div>
        </div>
      }
      <CustomModal
        title={'Configure Report Columns'.toTranslatedDisplay()}
        isVisible={showConfigureModal}
        onClose={closeModal}
        body={
          <TableConfigurator
            objectName={object || ''}
            screenName={screenName || ''}
            subObject={subObject}
            onChange={handleSave}
          />
        }
        id={''}
        onSave={() => {
        }}
      />
      <div style={{ position: 'relative', minHeight: isRefreshing && filteredData.length === 0 ? '150px' : undefined }}>
        {isRefreshing && (
          <div style={{
            position: 'absolute',
            inset: 0,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(var(--bs-body-bg-rgb), 0.6)',
            borderRadius: '4px',
          }}>
            <BeatLoader size={10} color='var(--bs-primary)' />
          </div>
        )}
      <div className="table-responsive" style={{ overflowY: 'auto' }}>
        <table className="w-100 table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3" {...getTableProps()} style={{ tableLayout: 'fixed', minWidth: '500px' }}>
          {/* MBAI-429: sticky header so column labels stay visible when scrolling long lists */}
          <thead className="fw-bold bg-secondary" style={{ position: 'sticky', top: 0, zIndex: 2 }}>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {showMultiSelect && (
                  <th className='p-2 me-2' style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      checked={selectedRows.length === filteredData.length}
                      style={{
                        width: '15px',  // Adjust the size of the checkbox
                        height: '15px',
                      }}
                    />
                  </th>
                )}


                {headerGroup.headers.map((column) => {
                  const { key, ...headerProps } = column.getHeaderProps(column.getSortByToggleProps());
                  return (
                    <th
                      key={key}
                      {...headerProps}
                      style={{ width: column.width, cursor: 'pointer' }}
                      className={`p-2 ${column.className || ''}`}
                    >
                      {column.render('Header')}
                      <span>
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <i className="bi bi-arrow-down"></i>
                          ) : (
                            <i className="bi bi-arrow-up"></i>
                          )
                        ) : (
                          ''
                        )}
                      </span>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row, index) => {
              prepareRow(row);
              const isSubheading = (row.original as any)?.isSubheading || false;
              const isHighlight = (row.original as any)?.isHighlight || false;
              const isSummary = (row.original as any)?.isSummary || false;
              const { key, ...rowProps } = row.getRowProps();
              const isChecked = selectedRows.some(
                (selected) => JSON.stringify(selected) === JSON.stringify(row.original)
              );
              return (
                <tr
                  key={index}
                  {...rowProps}
                  className={isSubheading ? 'fw-bold' : isHighlight ? ' fw-bold' : isSummary ? 'fw-bold bg-light-warning' : ''}
                  onClick={(e) => {
                    if ((e.target as HTMLElement).id !== `select-checkbox-${row.id}`) { // Check if the event target is not the checkbox
                      onRowClick && onRowClick(row.original);
                    }
                  }}
                  style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                >
                  {showMultiSelect &&

                    <td className='p-2 me-2' style={{ width: '40px' }}>

                      <input
                        id={`select-checkbox-${row.id}`}
                        type="checkbox"
                        checked={
                          selectedRows.some(
                            (selected) => JSON.stringify(selected) === JSON.stringify(row.original)
                          )
                        }
                        onChange={(e) => {
                          e.stopPropagation(); // Prevent click propagation
                          toggleRowSelection(row.original); // Call row selection handler
                        }}
                        style={{
                          width: '15px',  // Adjust the size of the checkbox
                          height: '15px',
                        }}
                      />
                    </td>

                  }

                  {row.cells.map((cell, index) => {
                    const { key, ...cellProps } = cell.getCellProps();
                    // MBAI-461: actions column holds a button/dropdown, not text — clipping it with
                    // overflow:hidden/ellipsis truncates the "View" button and hides the dropdown.
                    const isActionsCol = (cell.column as any).id === 'actions';
                    return (
                      <td
                        key={key}
                        className={`p-2 text-gray-900 ${isSummary ? 'fw-bold' : ''} fs-6 text-hover-primary ${cell.column.className || ''}`}
                        {...cellProps}
                        style={{
                          width: cell.column.width,
                          whiteSpace: isActionsCol ? 'nowrap' : 'wrap',
                          overflow: isActionsCol ? 'visible' : 'hidden',
                          textOverflow: isActionsCol ? 'unset' : 'ellipsis',
                        }}
                      >
                        {isSubheading && index > 0
                          ? ''
                          : typeof cell.value === 'number' && cell.column.className?.includes('n2')
                            ? cell.value.toFixed(2)
                            : cell.value instanceof Date
                              ? displayDate(cell.value) // Convert Date object to string
                              : cell.render('Cell')
                        }


                      </td>
                    );
                  })}
                </tr>

              );
            })}
            {/* MBAI-439: empty state when no rows and not loading */}
            {page.length === 0 && !isRefreshing && (
              <tr>
                <td
                  colSpan={columnsWithWidth.length + (showMultiSelect ? 1 : 0)}
                  className="text-center py-10"
                >
                  <div className="d-flex flex-column align-items-center gap-2">
                    <i className="bi bi-inbox fs-2x text-muted" />
                    <span className="text-muted fs-6">
                      {object
                        ? `${'No'.toTranslatedDisplay()} ${object} ${'records found'.toTranslatedDisplay()}`
                        : 'No records found'.toTranslatedDisplay()}
                    </span>
                    {emptyStateActions && emptyStateActions.length > 0 && (
                      <div className="d-flex gap-2 mt-2">
                        {emptyStateActions.map((action, idx) => (
                          <button
                            key={idx}
                            type="button"
                            className={`btn btn-sm ${idx === 0 ? 'btn-primary' : 'btn-light-primary'}`}
                            onClick={action.onClick}
                          >
                            {action.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            )}
            {showTotal && totals && (
              <tr className="fw-bold bg-light">
                {columnsWithWidth.map((column: any, index) => (
                  <td key={index} className={`p-2 ${column.className || ''}`}>
                    {column.total ? (
                      typeof column.Cell === 'function'
                        ? column.Cell({ value: totals[column.accessor as string], row: { original: {} } })
                        : totals[column.accessor as string]?.toFixed(2)
                    ) : index === 0 ? 'Total'.toTranslatedDisplay() : ''}
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>
      </div>
      {hidePagination !== true &&
        <div className="d-flex justify-content-between mt-3">
          <div>
            <select className="form-select" value={pageSize} onChange={handlePageSizeChange}>
              {[30, 100, 500].map(size => (
                <option key={size} value={size}>
                  {'Show'.toTranslatedDisplay()} {size}
                </option>
              ))}
            </select>
          </div>

          <div className="d-flex align-items-center">
            <button className="btn btn-sm btn-secondary me-2" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
              {'<<'}
            </button>
            <button className="btn btn-sm btn-secondary me-2" onClick={() => previousPage()} disabled={!canPreviousPage}>
              {'<'}
            </button>
            <span>
              {'Page'.toTranslatedDisplay()} {' '}
              <strong>
                {pageIndex + 1} of {pageOptions.length}
              </strong>{' '}
            </span>
            <button className="btn btn-sm btn-secondary ms-2" onClick={() => nextPage()} disabled={!canNextPage}>
              {'>'}
            </button>
            <button className="btn btn-sm btn-secondary ms-2" onClick={() => gotoPage(pageOptions.length - 1)} disabled={!canNextPage}>
              {'>>'}
            </button>
          </div>


        </div>
      }
    </>
  );
};
